import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { fetchCurrencies } from 'store/reducers/currencies'
import {
  fetchUserSummary,
  LoginStatuses,
  selectedAuthStatus,
  selectedErrAuthCode,
  selectedUser,
  selectedUserSummary,
} from 'store/reducers/users'

import { Bounce, ToastContainer, toast } from 'react-toastify'

import { useTranslation } from 'utils/use-translation'

import NavbarV2Left from '../../components/ui/account/nabvar-v2-left'

import NavbarV2Right from '../../components/ui/account/navbar-v2-right'

import { allWallets, fetchWallets, setAllTransactions, setIsWalletTransactionsSet } from '../../store/reducers/wallets'

import ButtonBlue from '../../components/ui/button/_color/_blue'

import Popup from '../../components/ui/popup'

import ProblemIcon from './icons/problem.svg'

import styles from './styles.module.scss'

import 'react-toastify/dist/ReactToastify.css'
import { fetchTransactions } from 'store/fetchers/users'
import Image from 'next/image'

import SuccessIcon from 'images/success-icon.png'
import CloseIcon from 'images/close-icon-white.png'

export default function AccountLayout({ children }) {
  const { t } = useTranslation()

  const authStatus = useAppSelector(selectedAuthStatus)
  const user = useAppSelector(selectedUser)
  const errCode = useAppSelector(selectedErrAuthCode)
  const [isWalletsLoading, setIsWalletsLoading] = useState<boolean>(true)
  const [isSavingsLoading, setIsSavinsLoading] = useState<boolean>(true)
  const [errorAuthPopupOpened, setErrorAuthPopupOpened] = useState<boolean>(false)

  const [isLoansLoading, setIsLoansLoading] = useState<boolean>(true)

  const intervalRef = useRef<number | null>(null)
  const isLoaded = useRef<boolean>(false)

  const wallets = useAppSelector(allWallets)
  const userSummary = useAppSelector(selectedUserSummary)
  const isShowed = useRef<boolean>(false)

  const dispatch = useAppDispatch()
  const [errMsg, setErrMsg] = useState<string>(null)
  const [errHeading, setErrHeading] = useState<string>(null)

  const router = useRouter()

  const scrollRef = useRef(null)

  useEffect(() => {
    if (user && user.toaster_text && sessionStorage.getItem('isToasterShowed') !== 'True' && !isShowed.current) {
      notify(user.toaster_category ?? 'info')
      isShowed.current = true
    }
  }, [user])

  useEffect(() => {
    if (authStatus === LoginStatuses.ERR) {
      setErrorAuthPopupOpened(true)
    } else {
      setErrorAuthPopupOpened(false)
    }
  }, [authStatus])

  const notify = (category: string) => {
    switch (category) {
      case 'success':
        toast.success(
          <div>
            <h4>{user.toaster_title ?? 'Success'}</h4>
            <p className={styles.toastText}>{user.toaster_text}</p>
          </div>,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Bounce,
            onClose: () => {
              sessionStorage.setItem('isToasterShowed', 'True')
            },
          },
        )
        break
      case 'info':
        toast.info(
          <div>
            <h4>{user.toaster_title ?? 'Info'}</h4>
            <p className={styles.toastText}>{user.toaster_text}</p>
          </div>,
          {
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Bounce,
            onClose: () => {
              sessionStorage.setItem('isToasterShowed', 'True')
            },
          },
        )
        break
      case 'warning':
        toast.warning(
          <div>
            <h4>{user.toaster_title ?? 'Warning'}</h4>
            <p className={styles.toastText}>{user.toaster_text}</p>
          </div>,
          {
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Bounce,
            onClose: () => {
              sessionStorage.setItem('isToasterShowed', 'True')
            },
          },
        )
        break
      case 'success_dark':
        toast.success(
          <div>
            <p className={styles.toastText}>{user.toaster_text}</p>
          </div>,
          {
            position: 'top-center',
            icon: <Image src={SuccessIcon} width={16} height={16} className={styles.toaster__icon} />,
            closeButton: <Image src={CloseIcon} width={12} height={12} />,
            className: styles.settings_toaster,
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
            onClose: () => {
              sessionStorage.setItem('isToasterShowed', 'False')
              isShowed.current = false
            },
          },
        )
      default:
        break
    }
  }

  useEffect(() => {
    if (authStatus === LoginStatuses.LOGOUT) {
      void router.push('/user/sign-in')
    }
  }, [authStatus])

  useEffect(() => {
    if (userSummary) {
      setIsLoansLoading(false)
      setIsSavinsLoading(false)
    }
  }, [userSummary])

  useEffect(() => {
    if (wallets && wallets?.length) {
      setIsWalletsLoading(false)
    }
  }, [wallets])

  const fetchAllTransactions = async () => {
    const res = await fetchTransactions({ accountType: 'wallet' })

    if (res?.length) {
      dispatch(setAllTransactions(res))
    }

    dispatch(setIsWalletTransactionsSet(true))
  }

  const fetchData = () => {
    dispatch(fetchUserSummary())
    dispatch(fetchWallets())
    fetchAllTransactions()
    dispatch(fetchCurrencies(false))
  }

  const fetchDataWithoutCurrencies = () => {
    dispatch(fetchUserSummary())
    fetchAllTransactions()
    dispatch(fetchWallets())
  }

  useEffect(() => {
    switch (errCode) {
      case '0':
        setErrMsg(t('please_check_connection'))
        setErrHeading(t('unable_connect'))
        break
      case '500':
        setErrMsg(t('please_try_reloading'))
        setErrHeading(t('error_occured'))
    }
  }, [errCode])

  useEffect(() => {
    if (typeof document !== 'undefined' && authStatus === LoginStatuses.ERR) {
      setErrorAuthPopupOpened(true)
    } else {
      setErrorAuthPopupOpened(false)
    }
  }, [authStatus, user])

  useEffect(() => {
    if (authStatus === LoginStatuses.LOGIN && user) {
      if (!isLoaded.current) {
        const now = new Date()
        const delay = 30000 - (now.getTime() % 30000)

        // первый запуск сразу
        fetchData()

        // запуск через delay, чтобы ровно в 00/30 сек
        const timeout = setTimeout(() => {
          intervalRef.current = window.setInterval(fetchDataWithoutCurrencies, 30000)

          isLoaded.current = true
        }, delay)

        const longInterval = window.setInterval(() => {
          dispatch(fetchCurrencies(false))
        }, 12 * 60 * 60 * 1000) // 12 hours

        return () => {
          clearTimeout(timeout)

          if (intervalRef.current) {
            clearInterval(intervalRef.current)
          }

          clearInterval(longInterval)
        }
      }
    }

    return undefined
  }, [authStatus, user])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [router])

  return (
    <div className={styles.account}>
      {errorAuthPopupOpened && (
        <Popup heading={t('we_have_problem')} isWithoutClose onClose={() => null}>
          <div className={styles.errWrapper}>
            <div className={styles.errWrapper__content}>
              <img src={ProblemIcon} alt="warning icon" />
              <div className={styles.text}>
                <h3>{errHeading}</h3>
                <p>{errMsg}</p>
              </div>
            </div>
            {errCode !== '0' && (
              <ButtonBlue className={styles.goToMainPageBtn} onClick={() => router.push('/')}>
                {t('go_main_page')}
              </ButtonBlue>
            )}
          </div>
        </Popup>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      {authStatus !== LoginStatuses.ERR && (
        <div className={styles.main} ref={scrollRef}>
          <NavbarV2Left className={styles.left} user={user} />
          <div className={styles.content}>{children}</div>
          <NavbarV2Right
            savings={userSummary?.earn}
            wallets={wallets}
            isWalletsLoading={isWalletsLoading}
            isLoansLoading={isLoansLoading}
            isSavingsLoading={isSavingsLoading}
            loans={userSummary?.loan}
            className={styles.right}
          />
        </div>
      )}
    </div>
  )
}
